import React from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    CircularProgress,
    LinearProgress,
    useTheme,
    alpha
} from '@mui/material';
import {
    CreditCard as CreditCardIcon,
    Security as SecurityIcon
} from '@mui/icons-material';

interface PaymentLoadingModalProps {
    open: boolean;
    onClose?: () => void;
    title?: string;
    message?: string;
    showProgress?: boolean;
    progress?: number;
}

const PaymentLoadingModal: React.FC<PaymentLoadingModalProps> = ({
    open,
    onClose,
    title = "Processando Pagamento",
    message = "Aguarde enquanto processamos seu pagamento...",
    showProgress = false,
    progress = 0
}) => {
    const theme = useTheme();

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: alpha(theme.palette.background.default, 0.8),
                    backdropFilter: 'blur(4px)',
                }
            }}
        >
            <DialogContent sx={{ p: 4, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                    {/* Ícone animado */}
                    <Box sx={{ position: 'relative', mb: 2 }}>
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': {
                                        transform: 'scale(1)',
                                        boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.7)}`,
                                    },
                                    '70%': {
                                        transform: 'scale(1.05)',
                                        boxShadow: `0 0 0 10px ${alpha(theme.palette.primary.main, 0)}`,
                                    },
                                    '100%': {
                                        transform: 'scale(1)',
                                        boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0)}`,
                                    },
                                },
                            }}
                        >
                            <CreditCardIcon sx={{ fontSize: 40, color: 'white' }} />
                        </Box>

                        {/* Spinner externo */}
                        <CircularProgress
                            size={100}
                            thickness={2}
                            sx={{
                                position: 'absolute',
                                top: -10,
                                left: -10,
                                color: theme.palette.primary.main,
                                animation: 'spin 2s linear infinite',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' },
                                },
                            }}
                        />
                    </Box>

                    {/* Título */}
                    <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {title}
                    </Typography>

                    {/* Mensagem */}
                    <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                        {message}
                    </Typography>

                    {/* Barra de progresso */}
                    {showProgress && (
                        <Box sx={{ width: '100%', maxWidth: 300 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Progresso
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {Math.round(progress)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 8,
                                    borderRadius: 4,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 4,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Indicadores de segurança */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <SecurityIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        <Typography variant="body2" color="text.secondary">
                            Transação segura e criptografada
                        </Typography>
                    </Box>

                    {/* Dica para o usuário */}
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            backgroundColor: alpha(theme.palette.info.main, 0.1),
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        }}
                    >
                        <Typography variant="body2" color="info.main" sx={{ fontWeight: 500 }}>
                            💡 Não feche esta janela durante o processamento
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default PaymentLoadingModal;
